import { HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Message.module.scss";

export type MessageProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
    /** Illustration or icon area (optional). */
    graphic?: ReactNode;
    /** Primary heading (required). */
    title: ReactNode;
    /** Supporting copy (optional). */
    description?: ReactNode;
    /** CTA row: pass `Button`(s) or links. */
    actions?: ReactNode;
    /**
     * Alignment of title, description, and graphic.
     * Figma static empty/error anatomy uses centered stacks.
     */
    align?: "center" | "start";
    /**
     * Flex justification for the actions row.
     * `center` matches the static message anatomy; `end` matches modal footers.
     */
    actionsAlign?: "center" | "start" | "end";
};

/**
 * Treez DS — Message (static empty / error pattern).
 *
 * Design: Figma `Message Anatomy` — file `qgFv3OQga6RQ4luvSFuGFP` (node `7215:52944`).
 * Spacing: 32px graphic → text, 8px title → body, 32px text → actions.
 */
export function Message({
    graphic,
    title,
    description,
    actions,
    align = "center",
    actionsAlign = "center",
    className,
    ...rest
}: MessageProps) {
    const hasActions = Boolean(actions);

    return (
        <div
            className={cx(
                styles.root,
                align === "start" && styles.alignStart,
                className,
            )}
            data-ui="message"
            {...rest}
        >
            {graphic ? <div className={styles.graphic}>{graphic}</div> : null}
            <div
                className={cx(
                    styles.text,
                    hasActions && styles.textBeforeActions,
                )}
            >
                <div className={styles.title}>{title}</div>
                {description ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
            </div>
            {actions ? (
                <div
                    className={cx(
                        styles.actions,
                        actionsAlign === "end" && styles.actionsEnd,
                        actionsAlign === "start" && styles.actionsStart,
                    )}
                >
                    {actions}
                </div>
            ) : null}
        </div>
    );
}

Message.displayName = "Message";
